import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Box,
  Divider,
  Autocomplete,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import PATSaveButton from "./Buttons/PATSaveButton";
import PATCloseButton from "./Buttons/PATCloseButton";
import DatePickerController from "./inputs/DatePickerController";
import CheckboxController from "./inputs/CheckboxController";
import TextFieldController from "./inputs/TextFieldController";
import { getService } from "../reducers/service";
import { notify } from "../reducers/NotifierReducer";
import { entityNamesForCustomId, serviceNames } from "../utils/constants";
import {
  emailFieldvalidation,
  isNotFutureDateTest,
  isValidDateTest,
  maxLengthMessage,
  renderCodeOption,
} from "../utils/functions";
import { createEmployee } from "../reducers/EmployeeReducer";
import { createApplicant } from "../reducers/ApplicantsReducer";
import { createRecordFor as createRecordForEnum } from "../utils/enums";
import {
  PATH_APPLICANTS,
  PATH_APPLICANTS_EDIT,
  PATH_EMPLOYEE,
  PATH_EMPLOYEE_EDIT,
} from "../utils/routeConstants";

const personSchema = yup.object().shape({
  source: yup.string().nullable().oneOf([null, "person", "phone", "email"]),

  firstName: yup
    .string()
    .max(100, maxLengthMessage(100))
    .when("source", {
      is: (val) => !val,
      then: (schema) => schema.required("First name is required"),
      otherwise: (schema) => schema.nullable(),
    }),
  lastName: yup
    .string()
    .max(100, maxLengthMessage(100))
    .when("source", {
      is: (val) => !val,
      then: (schema) => schema.required("Last name is required"),
      otherwise: (schema) => schema.nullable(),
    }),
  middleName: yup.string().trim().max(50, maxLengthMessage(50)).nullable(),
  suffix: yup.string().trim().max(100, maxLengthMessage(100)).nullable(),
  race: yup
    .string()
    .max(100, maxLengthMessage(100))
    .when("source", {
      is: (val) => !val,
      then: (schema) => schema.required("Race is required"),
      otherwise: (schema) => schema.nullable(),
    }),
  gender: yup
    .string()
    .max(100, maxLengthMessage(100))
    .when("source", {
      is: (val) => !val,
      then: (schema) => schema.required("Sex is required"),
      otherwise: (schema) => schema.nullable(),
    }),
  ethnicity: yup
    .string()
    .max(100, maxLengthMessage(100))
    .when("source", {
      is: (val) => !val,
      then: (schema) => schema.required("Ethnicity is required"),
      otherwise: (schema) => schema.nullable(),
    }),
  birthDate: isValidDateTest("Birth Date").concat(
    isNotFutureDateTest("Birth Date")
  ),

  infoPhone: yup
    .string()
    .nullable()
    .max(200, maxLengthMessage(200, "digits"))
    .when("source", {
      is: "phone",
      then: (schema) => schema.required("Phone is required"),
      otherwise: (schema) => schema.nullable(),
    }),
  contactTypePhone: yup
    .string()
    .trim()
    .max(10, maxLengthMessage(10))
    .nullable(),
  methodTypePhone: yup.string().trim().max(10, maxLengthMessage(10)).nullable(),
  phoneNotes: yup.string().trim().max(2000, maxLengthMessage(2000)).nullable(),
  phoneDefault: yup.boolean().nullable(),

  infoEmail: emailFieldvalidation.when("source", {
    is: "email",
    then: (schema) => schema.required("Email is required"),
    otherwise: (schema) => schema.nullable(),
  }),

  contactTypeEmail: yup
    .string()
    .trim()
    .max(10, maxLengthMessage(10))
    .nullable(),
  methodTypeEmail: yup.string().trim().max(10, maxLengthMessage(10)).nullable(),
  emailNotes: yup.string().trim().max(2000, maxLengthMessage(2000)).nullable(),
  emailDefault: yup.boolean().nullable(),
});

const PersonDialog = ({
  open,
  onClose,
  genderOptions,
  raceOptions,
  ethnicityOptions,
  contactInfoOptions,
  suffixOptions,
  createRecordFor = createRecordForEnum.Employee,
  source = null,
  ptsPersonID,
  updateNumberList,
  updateEmailList,
  setPtsPersonID,
}) => {
  const defaultEthnicityOption =
    ethnicityOptions.find((e) => e.isDefault)?.value || null;
  const defaultRaceOption = raceOptions.find((e) => e.isDefault)?.value || null;
  const defaultGenderOption =
    genderOptions.find((e) => e.isDefault)?.value || null;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(personSchema),
    defaultValues: {
      source: source,
      firstName: "",
      middleName: "",
      lastName: "",
      suffix: "",
      race: defaultRaceOption,
      gender: defaultGenderOption,
      birthDate: null,
      ethnicity: defaultEthnicityOption,
      contactTypeEmail: "",
      contactTypePhone: "",
      infoEmail: "",
      infoPhone: "",
      emailDefault: false,
      phoneDefault: false,
      methodTypeEmail: "Email",
      methodTypePhone: "Phone",
    },
  });
  const dispatch = useDispatch();
  const history = useHistory();
  const userAgency = useSelector((state) => state.user.userAgency);

  const handleClose = () => {
    reset();
    onClose();
  };

  const generateCustomId = async (
    generateIdFor = entityNamesForCustomId.people
  ) => {
    const data = {
      EntityName:
        generateIdFor === entityNamesForCustomId.people
          ? entityNamesForCustomId.people
          : entityNamesForCustomId.personnelEmployeeId,
      agencyID: userAgency,
    };
    const service = getService(serviceNames.customId);
    service.timeout = 20000;

    const id = await service.create(data);
    return id;
  };
  const onSubmit = async (data) => {
    const personContactService = getService("contact-info");
    if (source === "phone") {
      const newPhone = await personContactService.create({
        ptsParentID: ptsPersonID,
        PartyType: "Person",
        ContactMethod: "Phone",
        MethodType: data.contactTypePhone,
        Info: data.infoPhone,
        IsDefault: data.phoneDefault,
        Notes: data.phoneNotes,
      });
      dispatch(notify("New Contact Created", "success"));
      updateNumberList?.(newPhone);
      handleClose();
      return;
    }
    if (source === "email") {
      const newEmail = await personContactService.create({
        ptsParentID: ptsPersonID,
        PartyType: "Person",
        ContactMethod: "Email",
        MethodType: data.contactTypeEmail,
        Info: data.infoEmail,
        IsDefault: data.emailDefault,
        Notes: data.emailNotes,
      });
      dispatch(notify("New Email Created", "success"));
      updateEmailList?.(newEmail);
      handleClose();
      return;
    }
    const personId = await generateCustomId(entityNamesForCustomId.people);
    const fullName =
      `${data.firstName} ` +
      (data.middleName ? `${data.middleName} ` : "") +
      data.lastName;
    const newPersonData = {
      firstName: data.firstName || null,
      lastName: data.lastName || null,
      middleName: data.middleName || null,
      fullName,
      suffix: data.suffix || null,
      race: data.race || null,
      sex: data.gender || null,
      born: data.birthDate || null,
      ethnicity: data.ethnicity || null,
      personId: personId,
    };
    const personService = getService(serviceNames.searchPeople);
    const id = await personService.create({ person: newPersonData });

    if (source === "person") {
      setPtsPersonID(id);
    }

    if (id) {
      const personContactService = getService("contact-info");

      if (data.infoEmail) {
        const contactEmailData = {
          ptsParentID: id,
          PartyType: "Person",
          ContactMethod: "Email",
          MethodType: data.contactTypeEmail,
          Info: data.infoEmail,
          IsDefault: data.emailDefault,
          Notes: data.emailNotes,
        };
        await personContactService.create(contactEmailData);
      }

      if (data.infoPhone) {
        const contactPhoneData = {
          ptsParentID: id,
          PartyType: "Person",
          ContactMethod: "Phone",
          MethodType: data.contactTypePhone,
          Info: data.infoPhone,
          IsDefault: data.phoneDefault,
          Notes: data.phoneNotes,
        };
        await personContactService.create(contactPhoneData);
      }

      dispatch(notify("Person created successfully", "success"));

      if (!source) {
        if (createRecordFor === createRecordForEnum.Applicant) {
          const applicantData = {
            ptsPersonId: id,
            firstName: newPersonData.firstName,
            lastName: newPersonData.lastName || null,
            middleName: newPersonData.middleName || null,
            suffix: newPersonData.suffix || null,
            race: newPersonData.race || null,
            gender: newPersonData.sex || null,
            dob: newPersonData.born || null,
            email: data.infoEmail || null,
            phone: data.infoPhone || null,
            agencyId: userAgency || null,
          };

          dispatch(createApplicant(applicantData)).then((result) => {
            if (result?.id) {
              history.push(`${PATH_APPLICANTS_EDIT}${result.id}`);
            } else {
              history.push(`${PATH_APPLICANTS}`);
            }
          });
        } else if (createRecordFor === createRecordForEnum.Employee) {
          const employeeId = await generateCustomId(
            entityNamesForCustomId.personnelEmployeeId
          );
          if (employeeId?.includes("Agency ID does not match")) {
            dispatch(
              notify(
                "Unable to create employee record. No custom Employee ID has been configured for this agency.",
                "warning"
              )
            );
            onClose();
            return;
          }
          const employeeData = {
            ptsPersonId: id,
            employeeNum: employeeId || null,
            firstName: newPersonData.firstName,
            lastName: newPersonData.lastName || null,
            middleName: newPersonData.middleName || null,
            suffix: newPersonData.suffix || null,
            race: newPersonData.race || null,
            gender: newPersonData.sex || null,
            dob: newPersonData.born || null,
            email: data.infoEmail || null,
            phoneNo: data.infoPhone || null,
            agencyId: userAgency || null,
          };
          dispatch(createEmployee(employeeData)).then((result) => {
            if (result?.id) {
              history.push(`${PATH_EMPLOYEE_EDIT}${result.id}`);
            } else {
              history.push(`${PATH_EMPLOYEE}`);
            }
          });
        }
      }
    }
    reset();
    onClose();
  };

  const nameFields = [
    { name: "firstName", label: "First Name", type: "text", md: 2.4 },
    { name: "middleName", label: "Middle Name", type: "text", md: 2.4 },
    { name: "lastName", label: "Last Name", type: "text", md: 2.4 },
    {
      name: "suffix",
      label: "Suffix",
      type: "autocomplete",
      options: suffixOptions,
      md: 2.4,
    },
    {
      name: "race",
      label: "Race",
      type: "autocomplete",
      options: raceOptions,
      md: 2.4,
    },
    {
      name: "gender",
      label: "Sex",
      type: "autocomplete",
      options: genderOptions,
      md: 2.4,
    },
    { name: "birthDate", label: "Birth Date", type: "date", md: 2.4 },
    {
      name: "ethnicity",
      label: "Ethnicity",
      type: "autocomplete",
      options: ethnicityOptions,
      md: 2.4,
    },
  ];

  const contactFields = [
    {
      name: "methodTypeEmail",
      label: "Method Type",
      type: "text",
      md: 2.4,
      defaultValue: "Email",
      disabled: true,
    },
    {
      name: "contactTypeEmail",
      label: "Contact Type",
      type: "autocomplete",
      options: contactInfoOptions,
      md: 2.4,
    },
    { name: "infoEmail", label: "Info", type: "text", md: 2.4 },
    { name: "emailDefault", label: "Default", type: "checkbox", md: 2.4 },
    { name: "emailNotes", label: "Notes", type: "text", md: 2.4 },
    {
      name: "methodTypePhone",
      label: "Method Type",
      type: "text",
      md: 2.4,
      defaultValue: "Phone",
      disabled: true,
    },
    {
      name: "contactTypePhone",
      label: "Contact Type",
      type: "autocomplete",
      options: contactInfoOptions,
      md: 2.4,
    },
    {
      name: "infoPhone",
      label: "Info",
      type: "text",
      md: 2.4,
      isPhoneNumber: true,
    },
    { name: "phoneDefault", label: "Default", type: "checkbox", md: 2.4 },
    { name: "phoneNotes", label: "Notes", type: "text", md: 2.4 },
  ];

  const renderField = (field) => {
    switch (field.type) {
      case "text":
        return (
          <TextFieldController
            name={field.name}
            label={field.label}
            control={control}
            errors={errors}
            required={field.required}
            disabled={field.disabled}
            isPhoneNumber={field.isPhoneNumber}
          />
        );

      case "autocomplete":
        return (
          <Controller
            name={field.name}
            control={control}
            render={({ field: controllerField }) => {
              const selectedOption =
                (field.options || []).find(
                  (o) => o.value === controllerField.value
                ) || null;

              return (
                <Autocomplete
                  value={selectedOption}
                  options={field.options || []}
                  getOptionLabel={(option) => option?.label || ""}
                  isOptionEqualToValue={(o, v) => o.value === v.value}
                  onChange={(_, value) =>
                    controllerField.onChange(value?.value || "")
                  }
                  renderOption={renderCodeOption}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={field.label}
                      error={!!errors?.[field.name]}
                      helperText={errors?.[field.name]?.message}
                    />
                  )}
                  fullWidth
                />
              );
            }}
          />
        );

      case "date":
        return (
          <DatePickerController
            name={field.name}
            label={field.label}
            control={control}
            errors={errors}
          />
        );

      case "checkbox":
        return (
          <CheckboxController
            name={field.name}
            label={field.label}
            control={control}
            errors={errors}
            required={field.required}
            disabled={field.disabled}
          />
        );

      default:
        return null;
    }
  };

  const FieldsSection = ({ label, children }) => (
    <Box
      sx={{
        border: 1,
        borderRadius: 2,
        p: 2,
        mt: 1,
        borderColor: "grey.500",
      }}
    >
      <span
        style={{
          backgroundColor: "#1E8DFA",
          color: "white",
          display: "inline-block",
          padding: "2px 8px",
          fontWeight: 500,
          fontSize: "0.750rem",
          borderRadius: "4px",
          paddingBlock: 1,
        }}
      >
        {label}
      </span>
      <Divider sx={{ mt: 1, mb: 2 }} />
      {children}
    </Box>
  );

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
      <DialogTitle>Add Person</DialogTitle>
      <DialogContent>
        {(source === "person" || source === null) && (
          <FieldsSection label="NAME">
            <Grid container spacing={2}>
              {nameFields.map((field) => (
                <Grid item xs={12} sm={field.md || 3} key={field.name}>
                  {renderField(field)}
                </Grid>
              ))}
            </Grid>
          </FieldsSection>
        )}

        <FieldsSection label="CONTACT">
          <Grid container spacing={3}>
            {contactFields
              .filter((field) => {
                if (source === "phone") {
                  return field.name.toLowerCase().includes("phone");
                }
                if (source === "email") {
                  return field.name.toLowerCase().includes("email");
                }
                return true;
              })
              .map((field) => (
                <Grid item xs={12} sm={field.md || 3} key={field.name}>
                  {renderField(field)}
                </Grid>
              ))}
          </Grid>
        </FieldsSection>
      </DialogContent>
      <DialogActions>
        <PATSaveButton onClick={handleSubmit(onSubmit)} />
        <PATCloseButton onClick={handleClose} />
      </DialogActions>
    </Dialog>
  );
};

export default PersonDialog;
